/**
 * Liste des sites de poker online disponibles pour les bankrolls.
 * Utilisée dans AddBankrollModal et pour le mapping des logos (PokerSiteLogo).
 */
export const POKER_SITES_ONLINE = [
  'Winamax',
  'Unibet',
  'Betclic',
  'PMU',
  'Partypoker.fr',
  'Pokerstars.fr',
  'Pokerstars.com',
  'Partypoker.com',
  'GGPoker',
  '888 Poker',
  'ACR',
  'Coinpoker',
] as const;

export type PokerSiteOnline = (typeof POKER_SITES_ONLINE)[number];

/** Nom du site -> slug pour fichier logo (public/images/logos/{slug}.svg ou .png) */
const SITE_TO_LOGO_SLUG: Record<string, string> = {
  'Winamax': 'winamax',
  'Unibet': 'unibet',
  'Betclic': 'betclic',
  'PMU': 'pmu',
  'Partypoker.fr': 'partypoker-fr',
  'Pokerstars.fr': 'pokerstars-fr',
  'Pokerstars.com': 'pokerstars-com',
  'Partypoker.com': 'partypoker-com',
  'GGPoker': 'ggpoker',
  '888 Poker': '888-poker',
  'ACR': 'acr',
  'Coinpoker': 'coinpoker',
  // Alias pour compatibilité avec noms déjà en base
  'PokerStars.fr': 'pokerstars-fr',
  'PokerStars.com': 'pokerstars-com',
  '888poker': '888-poker',
  'Unibet Poker': 'unibet',
  'Betclic Poker': 'betclic',
  'PartyPoker': 'partypoker-com',
};

export function getPokerSiteLogoSlug(siteName: string): string | null {
  return SITE_TO_LOGO_SLUG[siteName] ?? null;
}

/** Slugs qui n'ont que du PNG (pas de SVG) — charger .png directement pour éviter 404 */
export const LOGO_PNG_ONLY_SLUGS = new Set<string>(['ggpoker', '888-poker', 'winamax', 'unibet', 'pmu', 'acr']);

/** Slugs qui utilisent uniquement le fichier local (SVG) — pas de favicon, pas d'initiale */
export const LOGO_LOCAL_ONLY_SLUGS = new Set<string>(['partypoker-fr', 'partypoker-com', 'coinpoker']);

/** Couleur de fond du conteneur quand on affiche le logo custom (les PNG ont déjà leur propre fond) */
export const LOGO_CUSTOM_BG: Record<string, string> = {
  'ggpoker': '#000000',
  '888-poker': '#000000',
  'winamax': '#000000',
  'unibet': '#000000',
  'pmu': '#003366',
  'partypoker-fr': '#ffffff',
  'partypoker-com': '#ffffff',
  'acr': '#ffffff',
  'coinpoker': '#000000',
};

/** URL du logo (favicon ou image) — chargé depuis le site officiel */
export const POKER_SITE_LOGO_URL: Record<string, string> = {
  'Winamax': 'https://www.winamax.fr/favicon.ico',
  'Unibet': 'https://www.unibet.fr/favicon.ico',
  'Betclic': 'https://www.betclic.fr/favicon.ico',
  'PMU': 'https://www.pmu.fr/favicon.ico',
  'Partypoker.fr': 'https://www.partypoker.fr/favicon.ico',
  'Pokerstars.fr': 'https://www.pokerstars.fr/favicon.ico',
  'Pokerstars.com': 'https://www.pokerstars.com/favicon.ico',
  'Partypoker.com': 'https://www.partypoker.com/favicon.ico',
  'GGPoker': 'https://www.ggpoker.com/favicon.ico',
  '888 Poker': 'https://www.888poker.com/favicon.ico',
  'ACR': 'https://www.americascardroom.eu/favicon.ico',
  'Coinpoker': 'https://www.coinpoker.com/favicon.ico',
  'PokerStars.fr': 'https://www.pokerstars.fr/favicon.ico',
  'PokerStars.com': 'https://www.pokerstars.com/favicon.ico',
  '888poker': 'https://www.888poker.com/favicon.ico',
  'Unibet Poker': 'https://www.unibet.fr/favicon.ico',
  'Betclic Poker': 'https://www.betclic.fr/favicon.ico',
  'PartyPoker': 'https://www.partypoker.com/favicon.ico',
};

/** Couleur de fond et initiale pour fallback quand pas de logo image */
export const POKER_SITE_LOGO_FALLBACK: Record<string, { bgColor: string; initial: string }> = {
  'Winamax': { bgColor: '#ffffff', initial: 'W' },
  'Unibet': { bgColor: '#00a651', initial: 'U' },
  'Betclic': { bgColor: '#e30613', initial: 'B' },
  'PMU': { bgColor: '#003366', initial: 'P' },
  'Partypoker.fr': { bgColor: '#00a651', initial: 'PP' },
  'Pokerstars.fr': { bgColor: '#003087', initial: 'PS' },
  'Pokerstars.com': { bgColor: '#003087', initial: 'PS' },
  'Partypoker.com': { bgColor: '#00a651', initial: 'PP' },
  'GGPoker': { bgColor: '#000000', initial: 'GG' },
  '888 Poker': { bgColor: '#ffcc00', initial: '888' },
  'ACR': { bgColor: '#1a1a2e', initial: 'ACR' },
  'Coinpoker': { bgColor: '#6366f1', initial: 'CP' },
  'PokerStars.fr': { bgColor: '#003087', initial: 'PS' },
  'PokerStars.com': { bgColor: '#003087', initial: 'PS' },
  '888poker': { bgColor: '#ffcc00', initial: '888' },
  'Unibet Poker': { bgColor: '#00a651', initial: 'U' },
  'Betclic Poker': { bgColor: '#e30613', initial: 'B' },
  'PartyPoker': { bgColor: '#00a651', initial: 'PP' },
  'Casino Barrière': { bgColor: '#1E3A8A', initial: 'C' },
  'Club Poker Paris': { bgColor: '#059669', initial: 'C' },
};
